import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { 
  FaCode, 
  FaCloud, 
  FaMobile, 
  FaDatabase, 
  FaShieldAlt, 
  FaRobot,
  FaArrowRight,
  FaCheck
} from 'react-icons/fa';
import { HiSparkles, HiLightningBolt, HiCube } from 'react-icons/hi';
import './Services.css';

const Services = () => {
  const [services, setServices] = useState([]);

  useEffect(() => {
    fetchServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get('/api/services');
      setServices(response.data.data);
    } catch (error) {
      console.error('Error fetching services:', error);
      // Fallback services
      setServices(defaultServices);
    }
  };

  const defaultServices = [
    {
      _id: '1',
      title: 'Custom Software Development',
      description: 'Tailored software solutions designed to meet your unique business requirements. From concept to deployment, we build robust applications that drive efficiency.',
      icon: 'FaCode',
      features: ['Full-stack development', 'API integration', 'Legacy system modernization', 'Quality assurance']
    },
    {
      _id: '2',
      title: 'Cloud Solutions',
      description: 'Leverage the power of cloud computing with our comprehensive cloud services. We help you migrate, optimize, and manage your cloud infrastructure.',
      icon: 'FaCloud',
      features: ['AWS, Azure, GCP', 'Cloud migration', 'Infrastructure as code', 'Cost optimization']
    },
    {
      _id: '3',
      title: 'Mobile App Development',
      description: 'Create stunning mobile experiences for iOS and Android. Our apps combine beautiful design with powerful functionality.',
      icon: 'FaMobile',
      features: ['Native & cross-platform', 'UI/UX design', 'App store optimization', 'Maintenance & support']
    },
    {
      _id: '4',
      title: 'Data & Analytics',
      description: 'Transform your data into actionable insights. Our analytics solutions help you make data-driven decisions with confidence.',
      icon: 'FaDatabase',
      features: ['Business intelligence', 'Data visualization', 'Predictive analytics', 'Data warehousing']
    },
    {
      _id: '5',
      title: 'Cybersecurity',
      description: 'Protect your digital assets with our comprehensive security solutions. We identify vulnerabilities and implement robust defenses.',
      icon: 'FaShieldAlt',
      features: ['Security audits', 'Penetration testing', 'Compliance consulting', 'Incident response']
    },
    {
      _id: '6',
      title: 'AI & Machine Learning',
      description: 'Harness the power of artificial intelligence to automate processes and gain competitive advantages.',
      icon: 'FaRobot',
      features: ['ML model development', 'Natural language processing', 'Computer vision', 'AI consulting']
    }
  ];

  const iconMap = {
    FaCode: <FaCode />,
    FaCloud: <FaCloud />,
    FaMobile: <FaMobile />,
    FaDatabase: <FaDatabase />,
    FaShieldAlt: <FaShieldAlt />,
    FaRobot: <FaRobot />
  };

  const gradients = [
    'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
    'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)',
    'linear-gradient(135deg, #10b981 0%, #06b6d4 100%)',
    'linear-gradient(135deg, #f59e0b 0%, #ec4899 100%)',
    'linear-gradient(135deg, #8b5cf6 0%, #06b6d4 100%)'
  ];

  const displayServices = services.length > 0 ? services : defaultServices;

  return (
    <div className="services-page">
      <Navbar />

      {/* Hero Section */}
      <section className="services-hero">
        <div className="hero-bg">
          <div className="hero-gradient-orb orb-1"></div>
          <div className="hero-gradient-orb orb-2"></div>
          <div className="hero-grid"></div>
        </div>
        <div className="container">
          <div className="hero-content">
            <div className="section-badge">
              <HiSparkles />
              Our Services
            </div>
            <h1 className="hero-title">
              Transforming Ideas Into <span className="gradient-text">Digital Excellence</span>
            </h1>
            <p className="hero-description">
              Comprehensive technology solutions designed to accelerate your growth 
              and drive innovation across your organization.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section services-grid-section">
        <div className="container">
          <div className="section-header">
            <div className="section-badge">
              <HiCube />
              What We Offer
            </div>
            <h2 className="section-title">
              End-to-End <span className="gradient-text">Solutions</span>
            </h2>
            <p className="section-description">
              From strategy to execution, we provide the expertise you need to succeed in the digital age.
            </p>
          </div>

          <div className="services-grid">
            {displayServices.map((service, index) => (
              <div 
                key={service._id} 
                className="service-card"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="service-card-inner">
                  <div 
                    className="service-icon"
                    style={{ background: gradients[index % gradients.length] }}
                  >
                    {iconMap[service.icon] || <FaCode />}
                    <div 
                      className="icon-glow" 
                      style={{ background: gradients[index % gradients.length] }}
                    ></div>
                  </div>
                  
                  <h3 className="service-title">{service.title}</h3>
                  <p className="service-description">{service.description}</p>
                  
                  {service.features && service.features.length > 0 && (
                    <ul className="service-features">
                      {service.features.slice(0, 4).map((feature, idx) => (
                        <li key={idx}>
                          <FaCheck className="check-icon" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="card-border-glow"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section process-section section-dark">
        <div className="container">
          <div className="section-header">
            <div className="section-badge">
              <HiLightningBolt />
              Our Process
            </div>
            <h2 className="section-title">
              How We <span className="gradient-text">Deliver</span>
            </h2>
            <p className="section-description">
              A proven methodology that ensures quality, transparency, and results.
            </p>
          </div>

          <div className="process-timeline">
            <div className="process-step">
              <div className="step-number">01</div>
              <div className="step-content">
                <h3>Discovery</h3>
                <p>We dive deep into your requirements, goals, and challenges to understand your vision.</p>
              </div>
            </div>
            <div className="process-step">
              <div className="step-number">02</div>
              <div className="step-content">
                <h3>Strategy</h3>
                <p>Our team crafts a detailed roadmap with clear milestones and deliverables.</p>
              </div>
            </div>
            <div className="process-step">
              <div className="step-number">03</div>
              <div className="step-content">
                <h3>Development</h3>
                <p>Agile development with regular updates and collaborative feedback loops.</p>
              </div>
            </div>
            <div className="process-step">
              <div className="step-number">04</div>
              <div className="step-content">
                <h3>Launch & Support</h3>
                <p>Seamless deployment followed by ongoing maintenance and optimization.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section cta-section">
        <div className="cta-bg">
          <div className="cta-gradient"></div>
        </div>
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">
              Ready to <span className="gradient-text">Get Started</span>?
            </h2>
            <p className="cta-description">
              Let's discuss how our services can help you achieve your goals.
            </p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-primary btn-glow">
                Get a Quote
                <FaArrowRight />
              </Link>
              <Link to="/solutions" className="btn btn-secondary">
                View Solutions
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Services;
